import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BUSINESS, NAV_LINKS } from "@/data/site";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/i18n";
import Icon from "@/components/Icon";
import { EASE } from "@/components/Reveal";

function LangToggle({ compact = false }) {
    const { lang, setLang } = useLang();
    return (
        <button
            data-testid={compact ? "mobile-lang-toggle" : "lang-toggle"}
            onClick={() => setLang(lang === "en" ? "bn" : "en")}
            className={`rounded-full border border-ink/25 font-bold text-ink/80 hover:border-solar hover:text-solar transition-colors duration-300 ${
                compact ? "px-5 py-3 text-base" : "px-3.5 py-2 text-xs"
            }`}
            aria-label={lang === "en" ? "Switch to Bengali" : "Switch to English"}
        >
            {lang === "en" ? "বাংলা" : "EN"}
        </button>
    );
}

export default function Navbar() {
    const { t } = useLang();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (href) => {
        setOpen(false);
        setTimeout(() => scrollToId(href), open ? 250 : 0);
    };

    return (
        <>
            <header
                data-testid="main-navbar"
                className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "glass shadow-[0_8px_40px_rgba(0,207,255,0.08)] py-3"
                        : "bg-transparent py-5"
                }`}
            >
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
                    <button
                        data-testid="nav-logo"
                        onClick={() => go("home")}
                        className="flex items-center gap-2.5 group"
                        aria-label="Farakka Broadband — home"
                    >
                        <img
                            src="/images/logo.png"
                            alt="Farakka Broadband logo"
                            className="w-10 h-10 rounded-full object-cover ring-1 ring-solar/50 shadow-[0_0_24px_rgba(255,210,31,0.25)] group-hover:shadow-[0_0_34px_rgba(255,210,31,0.45)] transition-shadow duration-300"
                        />
                        <span className="font-heading font-bold text-lg tracking-tight text-ink">
                            Farakka <span className="text-cyan2">Broadband</span>
                        </span>
                    </button>

                    <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.href}
                                data-testid={`nav-link-${l.href}`}
                                onClick={() => go(l.href)}
                                className="text-sm text-ice/70 hover:text-cyan2 transition-colors duration-300 font-medium"
                            >
                                {t.nav[l.href]}
                            </button>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <LangToggle />
                        <a
                            data-testid="nav-call-now"
                            href={BUSINESS.tel}
                            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-electric to-cyan2 px-5 py-2.5 text-sm font-bold text-white hover:shadow-[0_0_30px_rgba(0,207,255,0.45)] hover:scale-[1.03] active:scale-95 transition-all duration-300"
                        >
                            <Icon name="phone" size={15} />
                            {t.nav.call}
                        </a>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden w-10 h-10 rounded-lg glass flex items-center justify-center text-ink"
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                        >
                            <Icon name={open ? "x" : "menu"} size={20} />
                        </button>
                    </div>
                </div>
            </header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 lg:hidden glass"
                        style={{ background: "rgba(7,17,31,0.92)" }}
                    >
                        <div className="h-full flex flex-col justify-center px-10 gap-2">
                            {NAV_LINKS.map((l, i) => (
                                <motion.button
                                    key={l.href}
                                    data-testid={`mobile-nav-link-${l.href}`}
                                    initial={{ opacity: 0, x: -30 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ delay: 0.05 + i * 0.06, duration: 0.5, ease: EASE }}
                                    onClick={() => go(l.href)}
                                    className="text-left font-heading text-3xl font-bold text-white/85 hover:text-cyan2 py-2 transition-colors"
                                >
                                    <span className="font-mono2 text-xs text-cyan2/60 mr-4">
                                        0{i + 1}
                                    </span>
                                    {t.nav[l.href]}
                                </motion.button>
                            ))}
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.55, duration: 0.5, ease: EASE }}
                                className="mt-8 flex items-center gap-4"
                            >
                                <a
                                    data-testid="mobile-menu-call"
                                    href={BUSINESS.tel}
                                    className="inline-flex w-fit items-center gap-3 rounded-full bg-gradient-to-r from-electric to-cyan2 px-8 py-4 font-bold text-white"
                                >
                                    <Icon name="phone" size={18} />
                                    {t.nav.call} {BUSINESS.phoneDisplay}
                                </a>
                                <LangToggle compact />
                            </motion.div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
